// A puppet: a plazmi figure that plays actions (actions/) without a Life, for
// pages whose figures don't walk a floor plan (the Landing) and for the
// contact sheets (tools/placesheet.ts). It does what Life does with a pose:
// squash, lean and lift the body, spin it about its middle, pose the nubs
// (rig.ts), and wear the face the pose asks for. It can walk a straight line
// too, carrying what an action says.
import * as THREE from 'three';
import { plazmi } from '../chars.js';
import { finishShadows } from '../kit.js';
import { contactShadow, settleShadow } from '../contact.js';
import { rigOf, poseRig, restRig, type Rig } from '../rig.js';
import { Work } from './index.js';
import type { Pose } from '../moves.js';

const PIVOT = 0.48;
type Look = { color: string; glow: string; belly?: string | null; seed?: number };

export class Puppet {
  root = new THREE.Group(); squash = new THREE.Group(); spin = new THREE.Group(); body = new THREE.Group();
  private models = new Map<string, { m: THREE.Object3D; rig: Rig }>();
  private cur: { m: THREE.Object3D; rig: Rig } | null = null;
  private work: Work;
  private t = 0;
  // walking: from, to, how far along (m), speed (m/s)
  private walk: { ax: number; az: number; bx: number; bz: number; d: number; len: number; v: number; carry: string | null; then?: () => void } | null = null;
  private doing: string | null = null;
  emit: ((kind: string, at: THREE.Vector3, data?: any) => void) | null = null;
  private headV = new THREE.Vector3();

  private shade: THREE.Mesh | null = null;
  // shadow: a contact shadow this wide under it (P39), which shrinks and fades as it hops
  constructor(private look: Look, scale = 1, private restMood: string | null = null, shadow = 0) {
    if (shadow) { this.shade = contactShadow(shadow); this.root.add(this.shade); }
    this.root.add(this.squash); this.squash.add(this.spin); this.spin.add(this.body);
    this.spin.position.y = PIVOT * scale; this.body.position.y = -PIVOT * scale;
    this.body.scale.setScalar(scale);
    this.work = new Work(this.root, this.body, (out) => this.root.getWorldPosition(out), () => this.emit, () => this.head, () => this.root.visible);
    this.wear(restMood);
  }
  get head() { return this.root.getWorldPosition(this.headV).add(new THREE.Vector3(0, 1.4 * this.body.scale.y + this.squash.position.y, 0)); }
  get action() { return this.doing; }
  get walking() { return !!this.walk; }

  private wear(mood: string | null) {
    const key = String(mood);
    let v = this.models.get(key);
    if (!v) {
      const m = plazmi({ color: this.look.color, glow: this.look.glow, belly: this.look.belly ?? null, seed: this.look.seed ?? 1, mood } as any);
      finishShadows(m); this.body.add(m);
      // with a contact shadow under it, it casts none in the sun's pass; without, only the body itself
      // does (the eyes, nubs and feet in the shadow pass doubled the draw calls of a crowd)
      m.traverse((o: any) => { if (o.isMesh) { o.geometry.computeBoundingSphere(); if (this.shade || o.geometry.boundingSphere.radius * o.getWorldScale(new THREE.Vector3()).x < 0.2) o.castShadow = false; } });
      v = { m, rig: rigOf(m) }; this.models.set(key, v);
      m.visible = false;
    }
    if (this.cur !== v) { if (this.cur) this.cur.m.visible = false; v.m.visible = true; this.cur = v; }
    return v;
  }

  // play an action (null: stand)
  play(action: string | null) { this.doing = action; if (!this.walk) this.work.set(action); }
  // walk in a straight line to (x, z), carrying `carry` (an action) on the way; then `then`
  walkTo(x: number, z: number, o: { speed?: number; carry?: string | null; then?: () => void } = {}) {
    const ax = this.root.position.x, az = this.root.position.z, len = Math.hypot(x - ax, z - az);
    this.walk = { ax, az, bx: x, bz: z, d: 0, len, v: o.speed ?? 1.2, carry: o.carry ?? null, then: o.then };
    if (len > 0.01) this.root.rotation.y = Math.atan2(x - ax, z - az);
    this.work.set(o.carry ?? null);
  }
  face(x: number, z: number) { this.root.rotation.y = Math.atan2(x - this.root.position.x, z - this.root.position.z); }

  update(dt: number) {
    this.t += dt;
    let walkK = 0;
    const w = this.walk;
    if (w) {
      w.d = Math.min(w.len, w.d + w.v * dt);
      const k = w.len > 0 ? w.d / w.len : 1;
      this.root.position.x = w.ax + (w.bx - w.ax) * k; this.root.position.z = w.az + (w.bz - w.az) * k;
      walkK = 1;
      if (w.d >= w.len) { this.walk = null; this.work.set(this.doing); w.then?.(); }
    }
    const P = this.work.tick(dt);
    this.apply(P, walkK);
    if (this.shade) settleShadow(this.shade, this.squash.position.y, P ? P.sx : 1);
  }

  private apply(P: Pose | null, walkK: number) {
    // a waddle while walking: a bob, a sway, the feet stepping
    const ph = this.t * 9, bob = walkK * Math.abs(Math.sin(ph)) * 0.07, sway = walkK * Math.sin(ph) * 0.07;
    if (!P) {
      const v = this.wear(this.restMood);
      this.squash.scale.set(1, 1 + 0.015 * Math.sin(this.t * 2.2), 1);
      this.squash.position.set(0, bob, 0); this.squash.rotation.set(0, 0, sway); this.spin.rotation.set(0, 0, 0);
      restRig(v.rig);
      if (walkK) for (const f of v.rig.feet) f.o.position.y = f.p.y + 0.12 * Math.max(0, Math.sin(ph + (f.s > 0 ? 0 : Math.PI)));
      return;
    }
    const v = this.wear(P.mood ?? this.restMood);
    this.squash.scale.set(P.sx, P.sy, P.sx);
    this.squash.position.set(P.dx, P.y + bob, P.dz);
    this.squash.rotation.set(P.rx, 0, P.rz + sway);
    this.spin.rotation.set(P.px, P.py, P.pz);
    if (walkK) { P.kick[0] = Math.max(P.kick[0], 0.3 * Math.max(0, Math.sin(ph))); P.kick[1] = Math.max(P.kick[1], 0.3 * Math.max(0, -Math.sin(ph))); }
    poseRig(v.rig, P, this.t);
  }

  stop() { this.work.stop(); }
}
